import type {
  ClientCategory,
  LicenceCsm,
  LicenceDetail,
  LicenceResponse,
  LicenceStatsResponse,
} from '@shared/schemas';
import { all, call, getContext, put, takeLatest, takeLeading } from 'redux-saga/effects';

import { classifyInfraError, isNetworkError, NETWORK_ERROR_MESSAGE } from '@/utils/infra-error';

import { toLicencesError } from '../domain/licence-error';
import type { LicencesGateway } from '../infrastructure/licences.gateway';
import {
  clientCategoryAddRequested,
  deleteRequested,
  editOpenRequested,
  editSaveRequested,
  loadRequested,
  statsRequested,
  statusChangeRequested,
} from './licences.actions';
import { licencesActions } from './licences.slice';

const STATUS_ERROR = 'Failed to update the licence status.';
const DELETE_ERROR = 'Failed to delete the licence.';
const EDIT_ERROR = 'Failed to update licence.';

/**
 * WORKER: load the org's licences. All I/O goes through the injected gateway
 * (read from the saga `context`; never the api client), so tests drive success
 * and error branches with an in-memory gateway. `takeLatest` means a fresh
 * `loadRequested` supersedes any in-flight load.
 */
function* loadWorker() {
  const gateway: LicencesGateway = yield getContext('licencesGateway');
  yield put(licencesActions.loadStarted());
  try {
    const rows = (yield call(gateway.fetchLicences)) as LicenceResponse[];
    yield put(licencesActions.loadSucceeded(rows));
  } catch (e) {
    yield put(licencesActions.loadFailed(toLicencesError(e)));
  }
}

/**
 * WORKER: load the org-wide KPI figures. A separate lane from the rows: no
 * `loadStarted` and no error surfaced; the band is supporting info, so a failed
 * fetch just clears it (the page stays fully usable without the figures).
 */
function* statsWorker() {
  const gateway: LicencesGateway = yield getContext('licencesGateway');
  try {
    const stats = (yield call(gateway.fetchStats)) as LicenceStatsResponse;
    yield put(licencesActions.statsLoadSucceeded(stats));
  } catch {
    yield put(licencesActions.statsLoadFailed());
  }
}

/**
 * The backend's own words when it refused: "your own licence", "deactivate it
 * first", "one of its users owns another licence" are the whole message.
 */
function toMessage(e: unknown, fallback: string): string {
  if (isNetworkError(e)) return NETWORK_ERROR_MESSAGE;
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * WORKER: deactivate or reactivate a row's licence, patch the row in place, then
 * refetch the KPI figures: the Active and Inactive tiles count this licence.
 */
function* statusChangeWorker(action: ReturnType<typeof statusChangeRequested>) {
  const gateway: LicencesGateway = yield getContext('licencesGateway');
  yield put(licencesActions.mutationStarted('status'));
  try {
    const { revokedSessions } = (yield call(gateway.setLicenceStatus, action.payload)) as {
      revokedSessions: number;
    };
    yield put(licencesActions.statusChangeSucceeded({ ...action.payload, revokedSessions }));
    yield put(statsRequested());
  } catch (e) {
    yield put(licencesActions.mutationFailed(toMessage(e, STATUS_ERROR)));
  }
}

/** WORKER: delete a row's licence, drop the row, then refetch the KPI figures. */
function* deleteWorker(action: ReturnType<typeof deleteRequested>) {
  const gateway: LicencesGateway = yield getContext('licencesGateway');
  yield put(licencesActions.mutationStarted('delete'));
  try {
    yield call(gateway.deleteLicence, action.payload);
    yield put(licencesActions.deleteSucceeded(action.payload));
    yield put(statsRequested());
  } catch (e) {
    yield put(licencesActions.mutationFailed(toMessage(e, DELETE_ERROR)));
  }
}

/** The licence being edited, for its CSM; the form opens from the row meanwhile. */
function* editLicenceLoad(gateway: LicencesGateway, licenceId: string) {
  try {
    const licence = (yield call(gateway.loadLicence, licenceId)) as LicenceDetail;
    yield put(licencesActions.editLicenceLoaded(licence));
  } catch {
    yield put(licencesActions.editLicenceLoadFailed());
  }
}

/** Who the CSM select offers; "None" stays usable if this fails. */
function* csmCandidatesLoad(gateway: LicencesGateway) {
  yield put(licencesActions.csmCandidatesLoadStarted());
  try {
    const candidates = (yield call(gateway.loadCsmCandidates)) as LicenceCsm[];
    yield put(licencesActions.csmCandidatesLoadSucceeded(candidates));
  } catch {
    yield put(licencesActions.csmCandidatesLoadFailed());
  }
}

/** The client categories (#604); the licence's own stays offered if this fails. */
function* clientCategoriesLoad(gateway: LicencesGateway) {
  yield put(licencesActions.clientCategoriesLoadStarted());
  try {
    const categories = (yield call(gateway.loadClientCategories)) as ClientCategory[];
    yield put(licencesActions.clientCategoriesLoadSucceeded(categories));
  } catch {
    yield put(licencesActions.clientCategoriesLoadFailed());
  }
}

/**
 * WORKER: open Edit licence on a row: the three loads side by side, each
 * settling on its own so one failure never holds the others back.
 */
function* editOpenWorker(action: ReturnType<typeof editOpenRequested>) {
  const gateway: LicencesGateway = yield getContext('licencesGateway');
  yield put(licencesActions.editStarted(action.payload));
  yield all([
    call(editLicenceLoad, gateway, action.payload),
    call(csmCandidatesLoad, gateway),
    call(clientCategoriesLoad, gateway),
  ]);
}

/** WORKER: save the Edit licence form; the row takes the saved values. */
function* editSaveWorker(action: ReturnType<typeof editSaveRequested>) {
  const gateway: LicencesGateway = yield getContext('licencesGateway');
  yield put(licencesActions.editSaveStarted());
  try {
    const saved = (yield call(gateway.updateLicence, action.payload)) as LicenceDetail;
    yield put(licencesActions.editSaveSucceeded(saved));
  } catch (e) {
    yield put(licencesActions.editSaveFailed(toMessage(e, EDIT_ERROR)));
  }
}

/**
 * WORKER: add a client category from the Edit licence select (#604). A refusal
 * (a duplicate, an empty or too long name) is shown under the input as the
 * server words it.
 */
function* clientCategoryAddWorker(action: ReturnType<typeof clientCategoryAddRequested>) {
  const gateway: LicencesGateway = yield getContext('licencesGateway');
  yield put(licencesActions.clientCategoryAddStarted());
  try {
    const category = (yield call(gateway.addClientCategory, action.payload)) as ClientCategory;
    yield put(licencesActions.clientCategoryAddSucceeded(category));
  } catch (e) {
    yield put(
      licencesActions.clientCategoryAddFailed(
        classifyInfraError(e, "The category couldn't be added.").message,
      ),
    );
  }
}

/**
 * WATCHERS. The writes use `takeLeading`: each is confirmed in a dialog whose
 * button waits while it runs, so a second one is a double click, not a new intent.
 * Opening Edit is `takeLatest`: the licence opened last is the one on screen.
 */
export function* licencesSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(statsRequested.type, statsWorker),
    takeLeading(statusChangeRequested.type, statusChangeWorker),
    takeLeading(deleteRequested.type, deleteWorker),
    takeLatest(editOpenRequested.type, editOpenWorker),
    takeLeading(editSaveRequested.type, editSaveWorker),
    takeLeading(clientCategoryAddRequested.type, clientCategoryAddWorker),
  ]);
}
